import Vue from 'vue';
import lodashDebounce from 'lodash/debounce';

export class FormConfig {
  constructor(formTag, label, value, required = false, enabled = true, info = null) {
    this.formTag = formTag;
    this.label = label;
    this.value = value;
    this.required = required;
    this.enabled = enabled;
    this.isVisible = true;
    this.quantityUnit = '';
    this.info = info;
  }
}

export class FormBooleanConfig extends FormConfig {
  constructor(formTag, label, value, required = false, enabled = true) {
    super(formTag, label, value, required, enabled);
    this.component = () => import('@/components/Form/FormBoolInput.vue');
  }
}

export class FormTextAreaConfig extends FormConfig {
  constructor(formTag, label, value, required = false, enabled = true) {
    super(formTag, label, value, required, enabled);
    this.component = () => import('@/components/Form/FormTextAreaInput.vue');
  }
}

export class FormTextConfig extends FormConfig {
  constructor(formTag, label, value, required = false, enabled = true) {
    super(formTag, label, value, required, enabled);
    this.maxlength = undefined;
    this.component = () => import('@/components/Form/FormTextInput.vue');
  }
}

export class FormMaskedTextConfig extends FormConfig {
  constructor(formTag, label, value, mask, required = false, enabled = true) {
    super(formTag, label, value, required, enabled);
    this.mask = mask;
    this.component = () => import('@/components/Form/FormMaskedTextInput.vue');
  }
}
export class FormNumericConfig extends FormConfig {
  constructor(formTag, label, value, required = false, enabled = true) {
    super(formTag, label, value, required, enabled);
    this.component = () => import('@/components/Form/FormNumericInput.vue');
  }
  setMinMaxStep(min, max, step) {
    this.min = parseFloat(min);
    this.max = parseFloat(max);
    this.step = parseFloat(step);
    return this;
  }
}

export class FormSelectConfig extends FormConfig {
  constructor(formTag, label, value, options, required = false, enabled = true) {
    super(formTag, label, value, required, enabled);
    this.options = options;
    this.component = () => import('@/components/Form/FormSelectInput.vue');
  }
}

export class ListFormConfig extends FormConfig {
  constructor(formTag, label, value, listConfig, required = false, enabled = true) {
    super(formTag, label, value, required, enabled);
    this.listConfig = listConfig;
    this.component = () => import('@/components/Form/FormListInput.vue');
  }
}

export class DateTimeConfig extends FormConfig {
  constructor(formTag, label, value, showYear, showMonth, showDay, showHours, showMinutes, required = false, enabled = true) {
    super(formTag, label, value, required, enabled);
    this.showYear = showYear;
    this.showMonth = showMonth;
    this.showDay = showDay;
    this.showHours = showHours;
    this.showMinutes = showMinutes;
    this.component = () => import('@/components/Form/FormDateTimeInput.vue');
  }
}

export class FormColorConfig extends FormConfig {
  constructor(formTag, label, value, required = false, enabled = true) {
    super(formTag, label, value, required, enabled);
    this.component = () => import('@/components/ColorPicker.vue');
  }
}

export class FormControl {
  constructor () {
    this.storeTrigger = 400; // trigger mutate event every 400ms
    this.validationTrigger = 2000; // trigger validation event every 2s
    this.items = {};
    this.events = new Vue();
    this.mutationQueue = [];
    this._items = [];
    this.setup();
  }
  setup () {
    this.triggerUpdate = lodashDebounce(() => {
      this.emitChanges();
    }, this.storeTrigger);
    this.triggerValidate = lodashDebounce(() => {
      this.emitChanges();
      this.emitValidate();
    }, this.validationTrigger);
  }
  add (item) {
    this.items[item.formTag] = item;
    this._items.push(item);
  }
  get config () {
    return this.items;
  }
  getFormData() {
    let data = {};
    for (let prop in this.config) {
      data[prop] = this.config[prop].value;
    }
    return data;
  }
  async refreshVisibility () {
    for (let config in this.items) {
      let item = this.items[config];
      if (item.visibleFunc) {
        let val = await item.visibleFunc();
        if (typeof (val) === 'boolean') item.isVisible = val;
        if (typeof (val) === 'function') item.isVisible = val(item.value);
      }
    }
  }
  loadData(viewmodel) {
    if (viewmodel && viewmodel !== null) {
      for (let config in this.items) {
        let jsonvalue = viewmodel[config];
        this.items[config].value = jsonvalue;
      }
    }
  }
  mutate ({formTag, value}) {
    this.addMutation({formTag, value});
    this.triggerUpdate();
    this.triggerValidate();
  }
  addMutation ({formTag, value}) {
    let existing = this.mutationQueue.find(x => x.key === formTag);
    if (existing) {
      existing.value = value;
    } else {
      let change = {
        key: formTag,
        value: value
      };
      this.mutationQueue.push(change);
    }
  }
  emitChanges() {
    const queue = this.mutationQueue;
    if (queue && queue.length > 0) {
      this.events.$emit('mutate', queue);
      this.mutationQueue = [];
    }
  }
  emitValidate() {
    this.events.$emit('validate', this);
  }
  clearMutations() {
    this.mutationQueue = [];
  }
}

export class FocusHelper {
  constructor (vm) {
    this.vm = vm;
  }
  Focus(formitem) {
    try {
      this._validate(formitem);
      this.vm.$nextTick(() => {
        let component = null;
        let isFormWrapper = (formitem.$options.name === 'FormWrapper');
        if (isFormWrapper) {
          component = formitem.$refs.component;
        } else {
          component = formitem;
        }
        if (component) {
          let input = component.$refs.input;
          if (input) {
            let input2 = input.$refs ? input.$refs.input : null;
            if (input2 !== null) {
              input2.focus();
              this.vm.$nextTick(() => input2.select());
            } else {
              input.focus();
              input.select();
            }
          }
        }

      });

    } catch (error) {
      console.warn('Error trying to focus element', error);
    }
  }
  _validate (formitem) {
    if (!this.vm) throw new Error('vm not initialized');
    if (!formitem) throw new Error('formitem not defined');
    if (!formitem.$el) throw new Error('No $el found');
  }
}
